<template>
  <article>
    <line-chart
      :chart-data="chartData"
      :chart-options="chartOptions"
      :plugins="[htmlLegendPlugin]"
      style="min-height: 440px"
    />
    <div class="chart-bottom">
      <div :id="legendContainerId" class="d-flex align-center"></div>
      <div class="dataset-label">
        {{ datasetLabel }}
      </div>
    </div>
  </article>
</template>

<script>
  import { Line as LineChart } from 'vue-chartjs'
  import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    LineElement,
    LinearScale,
    CategoryScale,
    PointElement,
    TimeScale,
  } from 'chart.js'

  import 'chartjs-adapter-date-fns'
  import PeriodType from '@/models/enums/statistics/PeriodType'
  import { createTooltip, createLeftPart, createRightPart } from '@/util/revenue-dynamics/tooltip'
  import { positioningTooltip } from '@/util/chart'

  ChartJS.register(
    Title,
    Tooltip,
    Legend,
    LineElement,
    LinearScale,
    CategoryScale,
    PointElement,
    TimeScale,
  )

  export default {
    name: 'DynamicsChart',
    components: { LineChart },
    props: {
      legendContainerId: String,
      items: {
        type: Array,
        default: () => [],
      },
      periodType: {
        type: String,
        default: PeriodType.Week,
      },
    },
    data () {
      return {
        htmlLegendPlugin: {
          id: 'htmlLegend',
          afterUpdate (chart, args, options) {
            const legendContainer = document.getElementById(options.containerID)

            // Remove old legend items
            while (legendContainer.firstChild) {
              legendContainer.firstChild.remove()
            }

            const items = chart.options.plugins.legend.labels.generateLabels(chart)

            items.forEach((item, index) => {
              const label = document.createElement('div')
              label.style.alignItems = 'center'
              label.style.cursor = 'pointer'
              label.style.display = 'flex'
              label.style.flexDirection = 'row'

              if (index === 0) {
                label.style.marginRight = '30px'
              }

              label.onclick = () => {
                chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex))
                chart.update()
              }

              const rectangle = document.createElement('div')
              rectangle.style.background = item.fillStyle
              rectangle.style.borderColor = item.strokeStyle
              rectangle.style.borderWidth = item.lineWidth + 'px'
              rectangle.style.height = '23px'
              rectangle.style.marginRight = '10px'
              rectangle.style.borderRadius = '5px'

              rectangle.style.width = window.matchMedia('(max-width: 600px)').matches ? '70px' : '137px'

              const textContainer = document.createElement('p')
              textContainer.style.color = item.fontColor
              textContainer.style.margin = 0
              textContainer.style.padding = 0
              textContainer.style.textDecoration = item.hidden ? 'line-through' : ''

              textContainer.appendChild(document.createTextNode(item.text))

              label.appendChild(rectangle)
              label.appendChild(textContainer)
              legendContainer.appendChild(label)
            })
          },
        },
      }
    },
    computed: {
      chartOptions () {
        return {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: 'time',
              time: {
                unit: this.periodType === PeriodType.Year ? 'month' : 'day',
                tooltipFormat: 'yyyy-MM-dd',
                displayFormats: {
                  day: 'yyyy-MM-dd',
                  month: 'yyyy-MM',
                },
              },
              grid: {
                display: true,
                drawOnChartArea: false,
              },
              ticks: {
                source: 'data',
                callback: (value) => {
                  const date = new Date(value)

                  return this.periodType === PeriodType.Year
                    ? date.toLocaleDateString('ru', { month: 'short', year: 'numeric' })
                    : date.toLocaleDateString('ru', { day: 'numeric', month: 'short' })
                },
              },
            },
          },
          plugins: {
            htmlLegend: {
              containerID: this.legendContainerId,
            },
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              position: 'nearest',
              filter: function (item) {
                return item.chart.data.datasets[item.datasetIndex].showTooltip
              },
              external: this.externalTooltipHandler,
            },
          },
        }
      },
      chartData () {
        return {
          labels: this.items.map(item => item.date),
          datasets: [
            {
              label: 'Выручка за период',
              backgroundColor: '#FF9D00',
              borderColor: '#FF9D00',
              data: this.datasetData,
              cubicInterpolationMode: 'monotone',
              pointRadius: 2,
              pointHoverRadius: 8,
              showTooltip: true,
            },
            {
              label: 'Прошлый период',
              backgroundColor: '#D3D5D9',
              borderColor: '#D3D5D9',
              data: this.prevDatasetData,
              cubicInterpolationMode: 'monotone',
              pointRadius: 1,
              showTooltip: false,
            },
          ],
        }
      },
      datasetData () {
        return this.items.map(item => item.value)
      },
      prevDatasetData () {
        return this.items.map(item => item.prevValue)
      },
      datasetLabel () {
        if (!this.items.length) {
          return ''
        }

        if (this.items.length === 1) {
          return new Date(this.items[0].date).toLocaleDateString('ru', { day: 'numeric', month: 'numeric', year: 'numeric' })
        }

        const startDate = new Date(this.items[0].date).toLocaleDateString('ru', { day: 'numeric', month: 'numeric', year: 'numeric' })
        const endDate = new Date(this.items[this.items.length - 1].date).toLocaleDateString('ru', { day: 'numeric', month: 'numeric', year: 'numeric' })

        return startDate + ' - ' + endDate
      },
    },
    methods: {
      externalTooltipHandler (context) {
        const { chart, tooltip } = context
        const tooltipEl = createTooltip(chart)

        if (tooltip.opacity === 0) {
          tooltipEl.style.opacity = 0
          return
        }

        if (!tooltip.dataPoints.length) {
          return
        }

        // Remove old children
        while (tooltipEl.firstChild) {
          tooltipEl.firstChild.remove()
        }

        const date = tooltip.dataPoints[0].label
        const data = this.items.find((item) => item.date === date)

        tooltipEl.appendChild(createLeftPart(this.periodType, data))
        tooltipEl.appendChild(createRightPart(data))

        positioningTooltip(chart, tooltip, tooltipEl)
      },
    },
  }
</script>

<style lang="less" scoped>
.chart-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 15px;

  @media (max-width: 960px) {
    flex-direction: column-reverse;
  }
}
.dataset-label {
  font-size: 14px;
  color: #000;

  @media (max-width: 960px) {
    margin-bottom: 15px;
  }
}
</style>
